import { useEffect, useRef } from 'react';
import {
  waitingStudy,
  type IWaitingCanvas,
  type WaitingStudyId,
} from '@featit/shared/brand/waiting-motion';
import { waitingGround } from '@/components/Waiting';

function reducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * F-0579 ACC-9, ACC-11: a waiting study (`concept-waiting-motion.md`) used as an accent, not a wait.
 * With `settleMs` it plays the study's settle once, from its scattered start to its rest shape, and
 * then stays still (ACC-11's *Saved*); with none, or under Reduce Motion, it draws the rest shape at
 * once and never moves (ACC-9's empty state). It is drawn in the brand palette's tones for the
 * ground it sits on (WAIT-13), and is decoration: `aria-hidden`.
 */
export function StudyCanvas({
  study: id,
  sizePx,
  settleMs = 0,
  testId,
}: {
  study: WaitingStudyId;
  sizePx: number;
  settleMs?: number;
  testId?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(sizePx * dpr);
    canvas.height = Math.round(sizePx * dpr);
    const study = waitingStudy(id);
    const dark = document.documentElement.classList.contains('dark');
    const ground = waitingGround(canvas.parentElement, dark);
    const still = settleMs <= 0 || reducedMotion();
    const started = performance.now();
    let frame = 0;
    const paint = (now: number) => {
      const progress = still ? 1 : Math.min(1, (now - started) / settleMs);
      const k = 1 - Math.pow(1 - progress, 3);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, sizePx, sizePx);
      ctx.save();
      study.draw(ctx as unknown as IWaitingCanvas, {
        cx: sizePx / 2,
        cy: sizePx / 2,
        R: (sizePx / 2 - 1.2) / study.ext,
        t: 0,
        k,
        alpha: 1,
        dark,
        ground,
        minW: sizePx < 24 ? 1.2 : 1.6,
        compact: true,
      });
      ctx.restore();
      // Once at rest it is never drawn again (ACC-9, ACC-11: still).
      if (progress < 1) frame = requestAnimationFrame(paint);
    };
    if (still) paint(started);
    else frame = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(frame);
  }, [id, sizePx, settleMs]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-study={id}
      data-testid={testId}
      className="block shrink-0"
      style={{ width: sizePx, height: sizePx }}
    />
  );
}
